import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Dialog, Form, Link, useDebounce } from "@sscs/ui";
import { DOCS_URL, UNSUPPORTED_URL, URL_IMPORT_AUTH_TYPE } from "Constants";
import { useLazyValidateUrlImportQuery } from "Services";
import { UrlImportForm } from "Types";
import { requiresDockerHubCredentials } from "Utils";
import CommunityCard from "./CommunityCard";
import styles from "./urlmportForm.module.scss";
import { doesUrlSupportsAuth } from "./utils";

const SUPPORTED_URLS_DOCS_LINK = `${DOCS_URL}/filestream#supported-urls-and-purls`;

const UrlImportForm = (): React.JSX.Element => {
  const [validateUrl, { data }] = useLazyValidateUrlImportQuery();
  const {
    formState: { errors },
    getValues,
    setValue,
    unregister,
    watch,
  } = useFormContext<UrlImportForm>();
  const url = watch("url");
  const auth = watch("authType");
  const supportsAuth = doesUrlSupportsAuth(data?.purl ?? url);
  const requiresAuth = data?.purl
    ? requiresDockerHubCredentials(data.purl)
    : false;

  useDebounce(url, 250, (value) => {
    if (value && !errors.url) {
      validateUrl({ url: value }, true).then(({ data: validation }) => {
        if (!validation || getValues("url") !== value) return;

        const authType = getValues("authType");
        const canAuthenticate =
          validation.supports_download &&
          doesUrlSupportsAuth(validation.purl ?? value);

        if (!canAuthenticate && authType !== URL_IMPORT_AUTH_TYPE.none) {
          setValue("authType", URL_IMPORT_AUTH_TYPE.none, {
            shouldValidate: true,
          });
        } else if (
          canAuthenticate &&
          validation.purl &&
          requiresDockerHubCredentials(validation.purl) &&
          authType === URL_IMPORT_AUTH_TYPE.none
        ) {
          setValue("authType", URL_IMPORT_AUTH_TYPE.basic, {
            shouldValidate: true,
          });
        }
      });
    }
  });

  useEffect(() => {
    if (auth === URL_IMPORT_AUTH_TYPE.basic) {
      unregister("token");
    } else if (auth === URL_IMPORT_AUTH_TYPE.token) {
      unregister(["username", "password"]);
    } else if (auth === URL_IMPORT_AUTH_TYPE.none) {
      unregister(["username", "password", "token"]);
    }
  }, [auth, unregister]);

  let authFields: React.JSX.Element | null;

  switch (auth) {
    case URL_IMPORT_AUTH_TYPE.basic:
      authFields = (
        <>
          <Form.TextField
            name="username"
            label="Username"
            errors={errors}
            inputProps={{ autoComplete: "off" }}
          />
          <Form.TextField
            name="password"
            label="Password"
            inputProps={{ type: "password", autoComplete: "new-password" }}
            errors={errors}
          />
        </>
      );
      break;

    case URL_IMPORT_AUTH_TYPE.token:
      authFields = (
        <Form.TextField
          name="token"
          label="Bearer token"
          errors={errors}
          inputProps={{ autoComplete: "off" }}
        />
      );
      break;

    default:
      authFields = (
        <div className={styles.optional}>
          No authentication required for import
        </div>
      );
  }

  return (
    <>
      <Dialog.Section>
        <Dialog.Section.Description>
          Import a file by its direct HTTP/HTTPS URL to the file or a PURL.
          Supported sources: Spectra Assure Community, npm, PyPI, RubyGems,
          NuGet, VS Code, PS Gallery, Maven, Docker Hub, Docker Hardened Images,
          Amazon ECR, Hugging Face. Without a version, the latest one is
          imported.{" "}
          <Link
            underline="always"
            href={SUPPORTED_URLS_DOCS_LINK}
            target="_blank"
            rel="noreferrer"
            className={styles["docs-link"]}
          >
            Learn more
          </Link>
        </Dialog.Section.Description>
        <Dialog.Section.Content>
          <Form.TextField
            name="url"
            label="File URL or package URL (PURL)"
            isRequired
            inputProps={{ type: "url" }}
            errors={errors}
            renderError={(message) =>
              message === UNSUPPORTED_URL ? (
                <>
                  This URL isn’t supported.{" "}
                  <Link
                    id="url-import-learn-more"
                    underline="always"
                    href={SUPPORTED_URLS_DOCS_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className={styles["docs-link"]}
                  >
                    Learn more
                  </Link>
                </>
              ) : (
                message
              )
            }
          />
          {url && !errors.url && data && data.purl && (
            <CommunityCard purl={data.purl} />
          )}
        </Dialog.Section.Content>
      </Dialog.Section>
      {supportsAuth && data?.supports_download && !errors.url && (
        <Dialog.Section>
          <Dialog.Section.Title>
            {requiresAuth ? "Authentication" : "Authentication (Optional)"}
          </Dialog.Section.Title>
          <Dialog.Section.Content>
            <Form.RadioGroup
              name="authType"
              orientation="horizontal"
              errors={errors}
              items={[
                ...(requiresAuth
                  ? []
                  : [
                      {
                        value: URL_IMPORT_AUTH_TYPE.none,
                        label: "None",
                        id: "auth-none",
                      },
                    ]),
                {
                  value: URL_IMPORT_AUTH_TYPE.basic,
                  label: "Basic authentication",
                  id: "basic-auth",
                },
                {
                  value: URL_IMPORT_AUTH_TYPE.token,
                  label: "Token",
                  id: "auth-token",
                },
              ]}
            />
            {authFields}
          </Dialog.Section.Content>
        </Dialog.Section>
      )}
    </>
  );
};

export default UrlImportForm;
